/**
 * Distingue "entrando na plataforma" de "a plataforma recarregou com você já dentro".
 *
 * A diferença virou visível quando a troca de organização passou a recarregar a página: quem já
 * estava trabalhando via a tela de boas-vindas, com o mínimo de 900ms de animação, a cada troca.
 * Entrar é um momento; recarregar por dentro é uma espera — e espera não merece cerimônia.
 *
 * `sessionStorage` é a memória certa aqui: sobrevive à recarga da mesma aba e morre quando a aba
 * fecha, que é exatamente o ciclo de "estar dentro da plataforma".
 */
const CHAVE_DENTRO = "bd:sessao-iniciada";
const CHAVE_MOTIVO = "bd:recarga-motivo";

export type MotivoDaRecarga = "troca-organizacao";

const temSessionStorage = () => {
  try {
    return typeof window !== "undefined" && !!window.sessionStorage;
  } catch {
    // Safari em modo privado antigo lança ao tocar em sessionStorage.
    return false;
  }
};

/** Chamado depois que o usuário carregou — a partir daí, recarregar é "por dentro". */
export function marcarSessaoIniciada() {
  if (!temSessionStorage()) return;
  sessionStorage.setItem(CHAVE_DENTRO, "1");
}

export function jaEstavaDentro(): boolean {
  if (!temSessionStorage()) return false;
  return sessionStorage.getItem(CHAVE_DENTRO) === "1";
}

/** Marca o motivo logo antes de uma recarga provocada pelo produto, pra tela dizer o que está
 *  acontecendo em vez de um "Carregando" genérico. */
export function marcarRecarga(motivo: MotivoDaRecarga) {
  if (!temSessionStorage()) return;
  sessionStorage.setItem(CHAVE_MOTIVO, motivo);
}

/** Lê e apaga: o motivo vale pra recarga seguinte, uma vez só. */
export function consumirMotivoDaRecarga(): MotivoDaRecarga | null {
  if (!temSessionStorage()) return null;
  const valor = sessionStorage.getItem(CHAVE_MOTIVO);
  if (valor) sessionStorage.removeItem(CHAVE_MOTIVO);
  return valor === "troca-organizacao" ? valor : null;
}
